import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getPublicTools, type ToolDto } from '@/api/tool';
import TutorialSection from './components/TutorialSection';
import { fetchDictBatch } from '@/api/dict';
import { usePublicSubPageContent } from '@/hooks/usePublicSubPageContent';

function readText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function clampStars(value: unknown): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(5, Math.round(value)));
}

function resolveDownloadsText(tool: ToolDto): string | null {
  const text = readText(tool.downloads);
  if (text) return text;
  if (typeof tool.downloadCount === 'number' && Number.isFinite(tool.downloadCount)) {
    return `${tool.downloadCount}`;
  }
  return null;
}

type ToolView = ToolDto & { sortOrder?: number };

function ToolListHeader() {
  const { content: subPageContent, error } = usePublicSubPageContent();
  const content = subPageContent.toolList;

  return (
    <div className="bg-gradient-to-br from-[#1a0a00] to-[#7c2d12] pt-28 pb-16 px-6">
      <div className="max-w-7xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/70 text-sm mb-4">
          <i className="ri-tools-line"></i>
          {content.badge || '无'}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{content.title || '无'}</h1>
        <p className="text-white/60 max-w-xl mx-auto">{content.subtitle || '无'}</p>
        {error && <p className="text-xs text-red-300 mt-3">页头内容加载失败：{error}</p>}
      </div>
    </div>
  );
}

export default function ToolsPage() {
  const [tools, setTools] = useState<ToolView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dictCategories, setDictCategories] = useState<{ label: string; value: string }[]>([]);
  const [dictPlatforms, setDictPlatforms] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    fetchDictBatch(['tool_category', 'tool_platform'])
      .then((dicts) => {
        if (dicts['tool_category']) {
          setDictCategories(dicts['tool_category'].map((x) => ({ label: x.label, value: x.value })));
        }
        if (dicts['tool_platform']) {
          setDictPlatforms(dicts['tool_platform'].map((x) => ({ label: x.label, value: x.value })));
        }
      })
      .catch((err) => console.error('Failed to load tool categories and platforms in public catalog:', err));
  }, []);

  const getCategoryLabel = useCallback((cat: string) => {
    if (cat === '全部') return '全部';
    const found = dictCategories.find((c) => c.value === cat);
    return found ? found.label : cat;
  }, [dictCategories]);

  const getPlatformLabel = useCallback((plat: string) => {
    const found = dictPlatforms.find((p) => p.value.toLowerCase() === plat.toLowerCase());
    return found ? found.label : plat;
  }, [dictPlatforms]);

  const refreshTools = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await getPublicTools();
      const list = Array.isArray(res?.list) ? (res.list as ToolView[]) : [];
      setTools(list);
    } catch (err) {
      setTools([]);
      setLoadError(err instanceof Error ? err.message : '获取工具列表失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshTools();
    const handleCustom = () => void refreshTools();
    window.addEventListener('wanxiang_tools_updated', handleCustom);
    return () => {
      window.removeEventListener('wanxiang_tools_updated', handleCustom);
    };
  }, [refreshTools]);

  const toolCategories = useMemo(() => {
    const cats = Array.from(new Set(
      tools
        .map((t) => readText(t.category))
        .filter(Boolean),
    ));
    return ['全部', ...cats];
  }, [tools]);

  const allToolTags = useMemo(() => {
    const tags = tools.flatMap((t) => (t.tags ?? []).map((tag) => readText(tag)).filter(Boolean));
    return Array.from(new Set(tags));
  }, [tools]);

  const allPlatforms = useMemo(() => {
    const platforms = tools.flatMap((t) => (t.platform ?? []).map((p) => readText(p)).filter(Boolean));
    return Array.from(new Set(platforms));
  }, [tools]);

  const [activeCategory, setActiveCategory] = useState('全部');
  const [search, setSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [minStars, setMinStars] = useState(0);
  const [maxStars, setMaxStars] = useState(5);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };
  const togglePlatform = (p: string) => {
    setSelectedPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };
  const toggleCategory = (c: string) => {
    setSelectedCategories((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const resetAdvanced = () => {
    setSelectedTags([]);
    setSelectedPlatforms([]);
    setMinStars(0);
    setMaxStars(5);
    setSelectedCategories([]);
  };

  const hasAdvancedFilter =
    selectedTags.length > 0 ||
    selectedPlatforms.length > 0 ||
    minStars > 0 ||
    maxStars < 5 ||
    selectedCategories.length > 0;

  const PAGE_SIZE = 6;
  const [currentPage, setCurrentPage] = useState(1);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortedTools = useMemo(() => {
    return [...tools].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
    });
  }, [tools]);

  const filtered = useMemo(() => {
    const keyword = search.trim();
    return sortedTools.filter((tool) => {
      const category = readText(tool.category);
      const name = readText(tool.name);
      const description = readText(tool.description);
      const tags = (tool.tags ?? []).map((tag) => readText(tag)).filter(Boolean);
      const platforms = (tool.platform ?? []).map((p) => readText(p)).filter(Boolean);
      const stars = clampStars(tool.stars);

      const matchCat = activeCategory === '全部' || category === activeCategory;
      const matchSearch = !keyword || name.includes(keyword) || description.includes(keyword) || category.includes(keyword);
      const matchTags = selectedTags.length === 0 || selectedTags.every((tag) => tags.includes(tag));
      const matchPlatform = selectedPlatforms.length === 0 || selectedPlatforms.some((p) => platforms.includes(p));
      const matchStars = stars >= minStars && stars <= maxStars;
      const matchCategories = selectedCategories.length === 0 || selectedCategories.includes(category);

      return matchCat && matchSearch && matchTags && matchPlatform && matchStars && matchCategories;
    });
  }, [activeCategory, search, selectedTags, selectedPlatforms, minStars, maxStars, selectedCategories, sortedTools]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, search, selectedTags, selectedPlatforms, minStars, maxStars, selectedCategories]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const platformIcons: Record<string, string> = {
    Windows: 'ri-windows-line',
    windows: 'ri-windows-line',
    macOS: 'ri-apple-line',
    macos: 'ri-apple-line',
    Linux: 'ri-terminal-line',
    linux: 'ri-terminal-line',
    Docker: 'ri-box-3-line',
    docker: 'ri-box-3-line',
    Web: 'ri-global-line',
    web: 'ri-global-line',
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />
      <ToolListHeader />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="搜索工具名称、分类..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f97316] transition-colors"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {toolCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeCategory === cat
                    ? 'bg-[#f97316] text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-[#f97316] hover:text-[#f97316]'
                  }`}
              >
                {getCategoryLabel(cat)}
              </button>
            ))}
            <button
              onClick={() => setShowAdvanced((v) => !v)}
              className={`px-4 py-2 rounded-xl text-sm font-medium border cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-all ${showAdvanced || hasAdvancedFilter
                  ? 'bg-[#f97316]/10 border-[#f97316] text-[#f97316]'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-[#f97316] hover:text-[#f97316]'
                }`}
            >
              <i className="ri-equalizer-line"></i>
              高级筛选
              {hasAdvancedFilter && (
                <span className="w-4 h-4 flex items-center justify-center bg-[#f97316] text-white text-xs rounded-full">
                  {selectedTags.length + selectedPlatforms.length + selectedCategories.length + (minStars > 0 ? 1 : 0) + (maxStars < 5 ? 1 : 0)}
                </span>
              )}
            </button>
          </div>
        </div>

        {showAdvanced && (
          <div className="bg-white border border-[#f97316]/20 rounded-2xl p-5 mb-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                <i className="ri-equalizer-line text-[#f97316]"></i>
                高级筛选
              </h3>
              {hasAdvancedFilter && (
                <button onClick={resetAdvanced} className="text-xs text-red-400 hover:text-red-500 cursor-pointer flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-refresh-line"></i>重置筛选
                </button>
              )}
            </div>

            {activeCategory === '全部' && (
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">工具分类（可多选）</p>
                <div className="flex flex-wrap gap-2">
                  {toolCategories.filter((c) => c !== '全部').map((c) => {
                    const active = selectedCategories.includes(c);
                    return (
                      <button
                        key={c}
                        onClick={() => toggleCategory(c)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer whitespace-nowrap transition-all ${active ? 'bg-[#f97316] border-[#f97316] text-white' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-[#f97316] hover:text-[#f97316]'}`}
                      >
                        {active && <i className="ri-check-line mr-1"></i>}
                        {getCategoryLabel(c)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">按标签筛选（可多选）</p>
              <div className="flex flex-wrap gap-2">
                {allToolTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer whitespace-nowrap transition-all ${selectedTags.includes(tag)
                        ? 'bg-[#f97316] border-[#f97316] text-white'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-[#f97316] hover:text-[#f97316]'
                      }`}
                  >
                    {selectedTags.includes(tag) && <i className="ri-check-line mr-1"></i>}
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">支持平台（可多选，满足任一即可）</p>
                <div className="flex flex-wrap gap-2">
                  {allPlatforms.map((p) => (
                    <button
                      key={p}
                      onClick={() => togglePlatform(p)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border cursor-pointer whitespace-nowrap transition-all ${selectedPlatforms.includes(p)
                          ? 'bg-[#f97316] border-[#f97316] text-white'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-[#f97316] hover:text-[#f97316]'
                        }`}
                    >
                      <i className={`${platformIcons[p] || 'ri-computer-line'} text-sm`}></i>
                      {getPlatformLabel(p)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">社区评分范围</p>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button key={s} onClick={() => { setMinStars(s === minStars ? 0 : s); if (s > maxStars) setMaxStars(s); }} className="cursor-pointer">
                        <i className={`ri-star-fill text-lg ${s >= minStars && s <= maxStars && minStars > 0 ? 'text-[#f97316]' : 'text-gray-200'} hover:text-[#f97316] transition-colors`}></i>
                      </button>
                    ))}
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {minStars === 0 ? '不限' : `${minStars} ~ ${maxStars} 星`}
                  </span>
                </div>
                {minStars > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">至多：</span>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button key={s} onClick={() => { setMaxStars(s); if (s < minStars) setMinStars(s); }} className="cursor-pointer">
                          <i className={`ri-star-fill text-sm ${s <= maxStars ? 'text-[#f97316]' : 'text-gray-200'}`}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {hasAdvancedFilter && (
              <div className="pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                {selectedCategories.map((c) => (
                  <span key={c} className="flex items-center gap-1 px-2.5 py-1 bg-[#f97316]/10 text-[#f97316] text-xs rounded-full">
                    <i className="ri-folder-line text-xs"></i>{getCategoryLabel(c)}
                    <button onClick={() => toggleCategory(c)} className="cursor-pointer ml-0.5"><i className="ri-close-line text-xs"></i></button>
                  </span>
                ))}
                {selectedTags.map((t) => (
                  <span key={t} className="flex items-center gap-1 px-2.5 py-1 bg-[#f97316]/10 text-[#f97316] text-xs rounded-full">
                    {t}
                    <button onClick={() => toggleTag(t)} className="cursor-pointer ml-0.5">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                ))}
                {selectedPlatforms.map((p) => (
                  <span key={p} className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    <i className={`${platformIcons[p] || 'ri-computer-line'} text-xs`}></i>
                    {getPlatformLabel(p)}
                    <button onClick={() => togglePlatform(p)} className="cursor-pointer ml-0.5">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                ))}
                {minStars > 0 && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-[#f97316]/10 text-[#f97316] text-xs rounded-full">
                    {minStars}~{maxStars}星
                    <button onClick={() => { setMinStars(0); setMaxStars(5); }} className="cursor-pointer ml-0.5">
                      <i className="ri-close-line text-xs"></i>
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">
            共 <strong className="text-gray-900">{filtered.length}</strong> 个工具
            {hasAdvancedFilter && <span className="text-[#f97316] ml-1">（已应用高级筛选）</span>}
          </p>
          {totalPages > 1 && (
            <p className="text-sm text-gray-400">第 {currentPage} / {totalPages} 页</p>
          )}
        </div>

        {loadError && (
          <div className="mb-6 bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-500 flex items-center justify-between">
            <span>工具列表加载失败：{loadError}</span>
            <button onClick={() => void refreshTools()} className="text-xs px-2.5 py-1 rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors">
              重试
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-500">正在加载工具列表...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((tool) => {
                const id = String(tool.id ?? '');
                const name = readText(tool.name) || '无';
                const description = readText(tool.description) || '无';
                const category = readText(tool.category) || '无';
                const version = readText(tool.version);
                const size = readText(tool.size);
                const stars = clampStars(tool.stars);
                const tags = (tool.tags ?? []).map((tag) => readText(tag)).filter(Boolean);
                const platforms = (tool.platform ?? []).map((p) => readText(p)).filter(Boolean);
                const downloadsText = resolveDownloadsText(tool);

                return (
                  <div key={id} className="bg-white border border-gray-100 rounded-2xl p-6 hover:border-[#f97316]/30 transition-all duration-300 group relative">
                    {tool.pinned && (
                      <div className="absolute -top-0 left-1/2 -translate-x-1/2">
                        <span className="bg-gradient-to-r from-[#f97316] to-[#ef4444] text-white text-xs font-bold px-4 py-1 rounded-b-xl flex items-center gap-1 shadow-sm whitespace-nowrap">
                          <i className="ri-fire-fill"></i>热门推荐
                        </span>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#f97316]/15 to-[#f97316]/5 rounded-xl group-hover:from-[#f97316]/25 transition-all">
                        <i className="ri-tools-fill text-[#f97316] text-xl"></i>
                      </div>
                      {stars > 0 ? (
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className={`ri-star-fill text-xs ${i < stars ? 'text-[#f97316]' : 'text-gray-200'}`}></i>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">无评分</span>
                      )}
                    </div>

                    <h3 className="font-bold text-gray-900 text-base mb-1">{name}</h3>
                    <p className="text-gray-500 text-sm mb-4 line-clamp-2">{description}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-2 py-1 bg-[#f97316]/10 text-[#f97316] text-xs rounded-lg font-medium">{getCategoryLabel(category)}</span>
                      {version && <span className="px-2 py-1 bg-gray-50 text-gray-500 text-xs rounded-lg">v{version}</span>}
                      {size && <span className="px-2 py-1 bg-gray-50 text-gray-500 text-xs rounded-lg">{size}</span>}
                    </div>

                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {tags.map((tag) => (
                          <button
                            key={tag}
                            onClick={() => { toggleTag(tag); setShowAdvanced(true); }}
                            className={`px-2 py-0.5 text-xs rounded-md cursor-pointer transition-colors whitespace-nowrap ${selectedTags.includes(tag)
                                ? 'bg-[#f97316] text-white'
                                : 'bg-gray-50 text-gray-400 hover:bg-[#f97316]/10 hover:text-[#f97316]'
                              }`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    )}

                    {platforms.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-5">
                        {platforms.map((p) => (
                          <button
                            key={p}
                            onClick={() => { togglePlatform(p); setShowAdvanced(true); }}
                            className={`flex items-center gap-1 px-2 py-0.5 border text-xs rounded-md cursor-pointer transition-colors whitespace-nowrap ${selectedPlatforms.includes(p)
                                ? 'border-[#f97316] bg-[#f97316]/10 text-[#f97316]'
                                : 'border-gray-200 text-gray-500 hover:border-[#f97316] hover:text-[#f97316]'
                              }`}
                          >
                            <i className={`${platformIcons[p] || 'ri-computer-line'} text-xs`}></i>
                            {getPlatformLabel(p)}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                      <i className="ri-download-line"></i>
                      <span>下载量 {downloadsText || '无'}</span>
                    </div>

                    <div className="flex gap-2">
                      <Link
                        to={`/tools/${id}`}
                        className="flex-1 text-center py-2 bg-gray-50 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap border border-gray-200"
                      >
                        <i className="ri-information-line mr-1"></i>
                        详情
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-20 text-gray-400">
                <i className="ri-tools-line text-4xl mb-3 block"></i>
                <p className="mb-2">没有找到匹配的工具</p>
                {hasAdvancedFilter && (
                  <button onClick={resetAdvanced} className="text-sm text-[#f97316] hover:underline cursor-pointer">清除高级筛选条件</button>
                )}
              </div>
            )}
          </>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer transition-all"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }).map((_, i) => {
              const p = i + 1;
              if (totalPages > 7 && Math.abs(p - currentPage) > 2 && p !== 1 && p !== totalPages) {
                if (p === currentPage - 3 || p === currentPage + 3) return <span key={p} className="text-gray-300 px-1">...</span>;
                return null;
              }
              return (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm font-medium cursor-pointer transition-all ${currentPage === p
                      ? 'bg-[#f97316] text-white'
                      : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                >{p}</button>
              );
            })}
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer transition-all"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>

      <TutorialSection />
      <Footer />
    </div>
  );
}
